import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  type ExtraWorkedTimeRequestsResponse,
  extraWorkedTimeRequestsResponseSchema,
  ExtraWorkedTimeRequestStatus,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { ExtraWorkedTimeType } from "@src/appV2/Shifts/Shift/types";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

const fullShiftExtraWorkedTimeRequestsParamsSchema = z.object({
  "filter[shiftId]": z.string().optional(),
  "filter[type][]": z.nativeEnum(ExtraWorkedTimeType),
  "filter[status][]": z.array(z.nativeEnum(ExtraWorkedTimeRequestStatus)),
  "filter[updatedAt][gte]": z.string().optional(),
});

interface UseGetFullShiftExtraWorkedTimeRequestsParams {
  shiftId?: string;
  updatedAtFilter?: Date;
}

export function useGetFullShiftExtraWorkedTimeRequests(
  params: UseGetFullShiftExtraWorkedTimeRequestsParams = {},
  options: UseGetQueryOptions<ExtraWorkedTimeRequestsResponse> = {}
): UseQueryResult<ExtraWorkedTimeRequestsResponse> {
  const { shiftId, updatedAtFilter } = params;

  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/extra-worked-time-requests`,
    requestSchema: fullShiftExtraWorkedTimeRequestsParamsSchema,
    responseSchema: extraWorkedTimeRequestsResponseSchema,
    queryParams: {
      ...(shiftId ? { "filter[shiftId]": shiftId } : {}),
      "filter[type][]": ExtraWorkedTimeType.FULL_SHIFT,
      "filter[status][]": [
        ExtraWorkedTimeRequestStatus.APPROVED,
        ExtraWorkedTimeRequestStatus.DECLINED,
        ExtraWorkedTimeRequestStatus.PENDING,
      ],
      ...(updatedAtFilter ? { "filter[updatedAt][gte]": updatedAtFilter.toISOString() } : {}),
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_EXTRA_WORKED_TIME_REQUESTS_FAILURE,
    },
    ...options,
  });
}
